import React from 'react';
import { useRouter } from 'next/router';
import { useAjax } from '@developers/use-ajax';
import * as ordersAjax from '../ajax/orders';
import * as statesAjax from '../ajax/states';
import Head from 'next/head';
import FullDetails from '../components/OrderStatus/FullDetails';
import ValidationForm from '../components/OrderStatus/ValidationForm';
import { useServiceProviders } from '../hooks/useServiceProviders';

interface Validators {
  token: string;
  purchase_id: string;
}

interface Order {
  purchase_ship_date: string;
  purchase_ship_zip: string;
  purchase_ship_code: string;
}

const OrderStatus = () => {

  const [states, getStates, getStatesMeta] = useAjax(statesAjax.getStates, []);

  // Validation through form or URL - (JWT key or secure_id)
  const [validation, getValidatorToken, getValidatorTokenMeta] = useAjax(ordersAjax.getValidatorToken, {} as Validators);
  const [formValidation, getValidatorTokenFromForm, getValidatorTokenFromFormMeta] = useAjax(ordersAjax.getValidatorTokenFromForm, {} as Validators);

  // Order details
  const [order, getOrderDetails, getOrderDetailsMeta] = useAjax(ordersAjax.getOrderDetails, {} as Order);
  const [products, getProducts, getProductsMeta] = useAjax(ordersAjax.getProducts, []);
  const [creditCard, getCreditCardUsedOnOrder, getCreditCardUsedOnOrderMeta] = useAjax(ordersAjax.getCreditCardUsedOnOrder, {});
  const [trackingNumbers, getTrackingInfo, getTrackingInfoMeta] = useAjax(ordersAjax.getTrackingInfo, []);
  const [damageClaims, getDamageClaimInfo, getDamageClaimInfoMeta] = useAjax(ordersAjax.getDamageClaimInfo, []);
  const [coreReturns, getCoreReturnDetails, getCoreReturnDetailsMeta] = useAjax(ordersAjax.getCoreReturnDetails, []);
  const [returnAuthorizations, getReturnAuthorizationDetails, getReturnAuthorizationDetailsMeta] = useAjax(ordersAjax.getReturnAuthorizationDetails, []);

  // Order Management
  const [modifiedOrderRequest, modifyOrder, modifyOrderMeta] = useAjax(ordersAjax.modifyOrder, {});
  const [creditCardResponse, updateCreditCard, updateCreditCardMeta] = useAjax(ordersAjax.updateCreditCard, {});

  const [servicers, { getServicersByZip }, meta] = useServiceProviders();
  
  const [validators, setValidators] = React.useState({} as Validators);

  const router = useRouter();

  // Validate based on URL search params
  React.useEffect(() => {
    if (router.query.key) {
      getValidatorToken(router.query.key as string);
    }

    if (router.query.secure_id) {
      getValidatorToken('', router.query.secure_id as string);
    }
  }, [router]);

  // Set validation token needed for future ajax calls
  React.useEffect(() => {
    setValidators({ ...validation, ...formValidation });
  }, [validation, formValidation]);

  // Attempt to get basic order details
  React.useEffect(() => {
    if (validators.token) {
      getOrderDetails(validators.purchase_id, validators.token);
    }
  }, [validators]);

  // Token was valid and basic order details were retrieved. Get all other order info
  React.useEffect(() => {
    if (getOrderDetailsMeta.success) {
      if (order.purchase_ship_date) {
        getTrackingInfo(validators.purchase_id, validators.token);
        getDamageClaimInfo(validators.purchase_id, validators.token);
      }

      getStates();

      getCreditCardUsedOnOrder(validators.purchase_id, validators.token);

      if (order.purchase_ship_code != 'WC') {
        getServicersByZip(order.purchase_ship_zip, 30);
      }
      
      getProducts(validators.purchase_id, validators.token);
      getCoreReturnDetails(validators.purchase_id, validators.token);
      getReturnAuthorizationDetails(validators.purchase_id, validators.token);
    }
  }, [getOrderDetailsMeta.success]);

  React.useEffect(() => {
    if (modifyOrderMeta.success) {
      getOrderDetails(validators.purchase_id, validators.token);
    }
  }, [modifyOrderMeta.success]);

  React.useEffect(() => {
    if (updateCreditCardMeta.success) {
      getCreditCardUsedOnOrder(validators.purchase_id, validators.token);
    }
  }, [updateCreditCardMeta.success]);

  function submitOrderManagementForm(formData) {
    modifyOrder(validators.token, formData);
  }

  function submitCreditCardChangeForm(creditCardData) {
    updateCreditCardMeta.reset();
    updateCreditCard(validators.purchase_id, creditCardData, validators.token);
  }

  if (getValidatorTokenMeta.loading) {
    return <div></div>
  }

  if (getOrderDetailsMeta.success) {
    return (
      <>
        <Head>
          <title>Order Status | Fitness and Exercise Equipment Repair Parts</title>
          <meta
            name="description"
            content={'Check the status of your order and view order details.'}
          />
          <meta httpEquiv="content-language" content="en-us" />
        </Head>
        <FullDetails
          // Order Info
          order={order}
          states={states}
          products={products}
          creditCard={creditCard}
          validators={validators}
          coreReturns={coreReturns}
          damageClaims={damageClaims}
          trackingNumbers={trackingNumbers}
          returnAuthorizations={returnAuthorizations}
          servicers={servicers}
          servicersLoading={meta.loading}
          submitCreditCardChangeForm={submitCreditCardChangeForm}
          creditCardResponse={creditCardResponse}
          updateCreditCardMeta={updateCreditCardMeta}
          submitOrderManagementForm={submitOrderManagementForm}
          modifyOrderMeta={modifyOrderMeta}
          getCreditCardUsedOnOrderMeta={getCreditCardUsedOnOrderMeta}
        />
      </>
    );
  }

  if ((!router.query.key && !router.query.secure_id) || getValidatorTokenMeta.failure) {
    return (
      <>
        <Head>
          <title>Order Status | Fitness and Exercise Equipment Repair Parts</title>
          <meta
            name="description"
            content={'Check the status of your order and view order details.'}
          />
        </Head>
        <ValidationForm
          pageAction='view order status'
          getValidatorToken={getValidatorTokenFromForm}
          linkInvalid={getValidatorTokenMeta.failure}
          formInvalid={getOrderDetailsMeta.failure || getValidatorTokenFromFormMeta.failure}
        />
      </>
    );
  }

  return null
};

export default OrderStatus;
